import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Clock, Film, Loader2, Trash2 } from "lucide-react";
import { useApi } from "../hooks/use-api";
import { useWedding } from "../contexts/wedding-context";
import { queryKeys } from "../lib/query-keys";
import { cdnUrl } from "../lib/cdn";
import { cn } from "../lib/utils";

/**
 * "Yüklemelerim" — misafirin kendi yüklediği içerikler (onay bekleyen +
 * galeride olan) ve KALICI silme (KVKK, F9). Silinen içerik organizatörün
 * "geri al" kuyruğuna düşmez; galeriden ve canlı ekrandan da kalkar.
 * Onay bekleyen kareler CDN'den servis edilmez → yer tutucu gösterilir.
 */

interface MyUpload {
  id: string;
  kind: "image" | "video";
  status: "pending" | "approved" | "rejected";
  r2_key: string;
  created_at: string;
}

export function MyUploads() {
  const { t } = useTranslation();
  const { slug } = useWedding();
  const api = useApi();
  const queryClient = useQueryClient();
  const [busyId, setBusyId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const { data } = useQuery({
    queryKey: queryKeys.media.mine(slug),
    queryFn: async () => (await api.get<MyUpload[]>("/media/mine")).data ?? [],
  });

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 2500);
    return () => clearTimeout(timer);
  }, [toast]);

  const remove = useCallback(
    async (item: MyUpload) => {
      if (!window.confirm(t("media.mine.deleteConfirm"))) return;
      setBusyId(item.id);
      try {
        await api.del(`/media/${item.id}`);
        // Liste + galeri + aktivite aynı önekte (['media', slug]) → hepsi tazelenir.
        await queryClient.invalidateQueries({ queryKey: queryKeys.media.all(slug) });
        setToast({ msg: t("media.mine.deleted"), ok: true });
      } catch (err) {
        setToast({ msg: t("media.mine.deleteFailed", { error: err instanceof Error ? err.message : String(err) }), ok: false });
      } finally {
        setBusyId(null);
      }
    },
    [api, queryClient, slug, t],
  );

  const items = (data ?? []).filter((m) => m.status !== "rejected");
  if (items.length === 0) return null;

  return (
    <section aria-labelledby="my-uploads-h" className="space-y-3" data-testid="my-uploads">
      <div>
        <h3 id="my-uploads-h" className="text-base font-semibold text-foreground dark:text-white">
          {t("media.mine.title", { count: items.length })}
        </h3>
        <p className="text-xs text-muted-foreground mt-0.5">{t("media.mine.hint")}</p>
      </div>
      <ul className="grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-6">
        {items.map((item) => {
          const pending = item.status === "pending";
          return (
            <li key={item.id} className="relative aspect-square overflow-hidden rounded-xl bg-muted">
              {!pending && item.kind === "image" ? (
                <img src={cdnUrl(item.r2_key, { w: 320 })} alt="" loading="lazy" className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-muted-foreground">
                  {item.kind === "video" ? <Film className="h-6 w-6" aria-hidden="true" /> : <Clock className="h-6 w-6" aria-hidden="true" />}
                </div>
              )}
              <span
                className={cn(
                  "absolute bottom-1.5 left-1.5 rounded-full px-2 py-0.5 text-[10px] font-semibold",
                  pending ? "bg-amber-500/90 text-white" : "bg-emerald-600/90 text-white",
                )}
              >
                {pending ? t("media.mine.pending") : t("media.mine.approved")}
              </span>
              <button
                type="button"
                onClick={() => void remove(item)}
                disabled={busyId !== null}
                aria-label={t("media.mine.delete")}
                className="absolute right-1.5 top-1.5 flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur-sm transition-colors hover:bg-red-600 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
              >
                {busyId === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              </button>
            </li>
          );
        })}
      </ul>
      {toast && (
        <div
          role="status"
          className={cn(
            "fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-xl px-4 py-2.5 text-sm font-medium text-white shadow-lg pointer-events-none",
            toast.ok ? "bg-green-600" : "bg-red-600",
          )}
        >
          {toast.msg}
        </div>
      )}
    </section>
  );
}
